import React, { useRef, useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Check, Camera, ScanLine, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { QUALIFICHE, RUOLI_OP } from "../config/operatore";

const CONTRATTI = [["", "—"], ["determinato", "Determinato"], ["indeterminato", "Indeterminato"], ["stagista", "Stagista"], ["cooperativa", "Cooperativa"]];
const ROLES = [["admin", "Admin aziendale"], ["responsabile", "Responsabile"], ["coordinatore", "Coordinatore"], ["preposto", "Preposto"], ["segreteria", "Segreteria"], ["oss", "OSS / OSA"], ["medico", "Medico / Infermiere"]];

const PhotoBox = ({ label, file, onPick, testid }) => {
  const ref = useRef(null);
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  return (
    <div className="flex-1 min-w-[150px]">
      <Label className="text-xs font-semibold mb-1 block">{label}</Label>
      <button type="button" onClick={() => ref.current?.click()} data-testid={testid}
        className={`w-full h-28 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 text-sm overflow-hidden transition-colors ${file ? "border-primary bg-primary/5 text-primary" : "border-muted text-muted-foreground hover:border-primary/50"}`}>
        {preview ? <img src={preview} alt={label} className="h-full w-full object-cover rounded-md" /> : <><Camera size={22} /> Scatta / carica</>}
      </button>
      {file && <p className="text-[11px] text-primary mt-1 flex items-center gap-1"><Check size={12} /> {file.name}</p>}
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onPick(e.target.files?.[0] || null)} />
    </div>
  );
};

export default function CheckInOperatore() {
  const { structures, selectedStructure, can } = useAuth();
  const nav = useNavigate();
  const [saving, setSaving] = useState(false);
  const [newOp, setNewOp] = useState(null);
  const [acc, setAcc] = useState({ email: "", password: "", role: "oss" });
  const [creating, setCreating] = useState(false);
  const canUtenti = can && can("utenti");
  const [fronte, setFronte] = useState(null);
  const [retro, setRetro] = useState(null);
  const [tessera, setTessera] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [f, setF] = useState({
    nome: "", cognome: "", qualifica: "OSS", ruolo: "", sesso: "", data_nascita: "", luogo_nascita: "", codice_fiscale: "",
    indirizzo: "", n_documento: "", telefono: "", email: "", pin: "", data_assunzione: "", contratto: "",
    ore_mensili: "", contratto_inizio: "", scadenza_contratto: "", note: "", stato: "attivo",
    structure_ids: selectedStructure && selectedStructure !== "all" ? [selectedStructure] : [],
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const scan = async () => {
    if (!fronte && !retro && !tessera) { toast.error("Carica almeno una foto del documento"); return; }
    setScanning(true);
    try {
      const fd = new FormData();
      if (fronte) fd.append("fronte", fronte);
      if (retro) fd.append("retro", retro);
      if (tessera) fd.append("tessera", tessera);
      const { data } = await api.post("/checkin/ocr", fd);
      const mapped = { ...data, n_documento: data.numero_documento };
      delete mapped.numero_documento;
      setF((p) => ({ ...p, ...Object.fromEntries(Object.entries(mapped).filter(([, v]) => v)) }));
      toast.success("Dati estratti dal documento — controlla e completa");
    } catch (e) { toast.error(e.response?.data?.detail || "Estrazione non riuscita: compila manualmente"); }
    finally { setScanning(false); }
  };

  const senzaScad = f.contratto === "indeterminato";

  const generatePin = async () => { try { const { data } = await api.get("/manage/operatore-pin"); if (data.pin) set("pin", data.pin); } catch { /* ignora */ } };
  useEffect(() => { generatePin(); /* eslint-disable-next-line */ }, []);

  const finish = async () => {
    if (!f.nome.trim() || !f.cognome.trim()) return toast.error("Nome e cognome sono obbligatori");
    if (!f.structure_ids.length) return toast.error("Seleziona almeno una struttura");
    if (!f.pin || f.pin.length !== 2) return toast.error("Imposta un PIN di 2 cifre per la Firma Rapida");
    setSaving(true);
    try {
      const payload = { ...f, structure_id: f.structure_ids[0], ore_mensili: f.ore_mensili ? Number(f.ore_mensili) : null };
      if (senzaScad) payload.scadenza_contratto = "";
      const { data } = await api.post("/operatori", payload);
      toast.success("Operatore registrato con successo");
      if (canUtenti) {
        setAcc({ email: f.email || "", password: "", role: "oss" });
        setNewOp({ id: data.id, nome: f.nome, cognome: f.cognome, structure_ids: f.structure_ids });
      } else {
        nav(`/operatori/${data.id}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const skipAccount = () => { const id = newOp?.id; setNewOp(null); nav(`/operatori/${id}`); };
  const createAccount = async () => {
    if (!acc.email.trim()) return toast.error("Inserisci un'email per l'accesso");
    setCreating(true);
    try {
      await api.post("/manage/utenti", {
        name: `${newOp.cognome} ${newOp.nome}`.trim(), email: acc.email.trim(),
        password: acc.password || undefined, role: acc.role,
        structure_ids: newOp.structure_ids, operatore_id: newOp.id,
      });
      toast.success("Account creato e collegato all'operatore");
      const id = newOp.id; setNewOp(null); nav(`/operatori/${id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nella creazione dell'account"); }
    finally { setCreating(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="checkin-operatore-page">
      <div className="mb-5">
        <h1 className="text-2xl font-head font-bold flex items-center gap-2"><UserPlus size={22} className="text-primary" /> Check in operatori</h1>
        <p className="text-sm text-muted-foreground">Registra un nuovo operatore: dati anagrafici, contatti e contratto.</p>
      </div>

      <div className="gr-card p-5 space-y-5">
        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><ScanLine size={14} /> Fotografa i documenti (opzionale)</div>
          <div className="flex gap-3 flex-wrap">
            <PhotoBox label="Carta d'identità (fronte)" file={fronte} onPick={setFronte} testid="cop-photo-fronte" />
            <PhotoBox label="Carta d'identità (retro)" file={retro} onPick={setRetro} testid="cop-photo-retro" />
            <PhotoBox label="Tessera sanitaria" file={tessera} onPick={setTessera} testid="cop-photo-tessera" />
          </div>
          <Button onClick={scan} disabled={scanning} className="mt-3 font-semibold" data-testid="cop-scan">
            {scanning ? <><Loader2 size={16} className="mr-1 animate-spin" /> Lettura in corso...</> : <><ScanLine size={16} className="mr-1" /> Estrai dati dal documento</>}
          </Button>
          <p className="text-xs text-muted-foreground mt-2">📷 Fotocamera non disponibile? Nessun problema: puoi compilare o correggere manualmente tutti i campi qui sotto.</p>
        </div>

        <div className="border-t pt-4">
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-3">Dati anagrafici</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold mb-1 block">Nome *</Label><Input value={f.nome} onChange={(e) => set("nome", e.target.value)} data-testid="cop-nome" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Cognome *</Label><Input value={f.cognome} onChange={(e) => set("cognome", e.target.value)} data-testid="cop-cognome" /></div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Qualifica (mansione)</Label>
              <select value={f.qualifica} onChange={(e) => set("qualifica", e.target.value)} className="h-10 w-full rounded-md border px-3 text-sm" data-testid="cop-qualifica">
                {QUALIFICHE.map((q) => <option key={q} value={q}>{q}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Ruolo (livello)</Label>
              <select value={f.ruolo} onChange={(e) => set("ruolo", e.target.value)} className="h-10 w-full rounded-md border px-3 text-sm" data-testid="cop-ruolo">
                <option value="">Seleziona…</option>
                {RUOLI_OP.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold mb-1 block">Strutture * (una, più o tutte)</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="cop-strutture">
                {structures.filter((s) => s.id !== "all").map((s) => { const sel = f.structure_ids.includes(s.id); return (
                  <button type="button" key={s.id} data-testid={`cop-struttura-${s.id}`} onClick={() => set("structure_ids", sel ? f.structure_ids.filter((x) => x !== s.id) : [...f.structure_ids, s.id])}
                    className={`text-xs font-semibold rounded-full border px-3 py-1 transition-colors ${sel ? "bg-primary text-white border-primary" : "hover:bg-secondary"}`}>{s.nome}</button>
                ); })}
                <button type="button" data-testid="cop-struttura-tutte" onClick={() => { const all = structures.filter((s) => s.id !== "all").map((s) => s.id); setF((p) => ({ ...p, structure_ids: p.structure_ids.length === all.length ? [] : all })); }}
                  className="text-xs font-semibold rounded-full border border-dashed px-3 py-1 hover:bg-secondary">Tutte le strutture</button>
              </div>
            </div>
            <div><Label className="text-xs font-semibold mb-1 block">Sesso</Label><Input value={f.sesso} onChange={(e) => set("sesso", e.target.value.toUpperCase())} placeholder="M / F" data-testid="cop-sesso" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Data di nascita</Label><Input type="date" value={f.data_nascita} onChange={(e) => set("data_nascita", e.target.value)} data-testid="cop-nascita" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Luogo di nascita</Label><Input value={f.luogo_nascita} onChange={(e) => set("luogo_nascita", e.target.value)} data-testid="cop-luogo" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Codice fiscale</Label><Input value={f.codice_fiscale} onChange={(e) => set("codice_fiscale", e.target.value.toUpperCase())} data-testid="cop-cf" /></div>
            <div className="sm:col-span-2"><Label className="text-xs font-semibold mb-1 block">Indirizzo di residenza</Label><Input value={f.indirizzo} onChange={(e) => set("indirizzo", e.target.value)} data-testid="cop-indirizzo" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">N° carta d'identità</Label><Input value={f.n_documento} onChange={(e) => set("n_documento", e.target.value)} data-testid="cop-doc" /></div>
          </div>
        </div>

        <div className="border-t pt-4">
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-3">Contatti e accesso</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label className="text-xs font-semibold mb-1 block">Telefono</Label><Input value={f.telefono} onChange={(e) => set("telefono", e.target.value)} data-testid="cop-telefono" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Email</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="cop-email" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">PIN Firma Rapida * (2 cifre)</Label>
              <div className="flex gap-2">
                <Input value={f.pin} onChange={(e) => set("pin", e.target.value.replace(/\D/g, "").slice(0, 2))} placeholder="es. 42" data-testid="cop-pin" />
                <Button type="button" variant="outline" onClick={generatePin} data-testid="cop-pin-gen" className="shrink-0">Genera</Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Necessario per la Firma Rapida su tablet. Ne abbiamo suggerito uno libero: modificabile.</p>
            </div>
          </div>
        </div>

        <div className="border-t pt-4">
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-3">Contratto</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold mb-1 block">Tipo contratto</Label>
              <select value={f.contratto} onChange={(e) => set("contratto", e.target.value)} className="h-10 w-full rounded-md border px-3 text-sm" data-testid="cop-contratto">
                {CONTRATTI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div><Label className="text-xs font-semibold mb-1 block">Ore mensili da contratto</Label><Input type="number" value={f.ore_mensili} onChange={(e) => set("ore_mensili", e.target.value)} placeholder="es. 160" data-testid="cop-ore" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Data assunzione</Label><Input type="date" value={f.data_assunzione} onChange={(e) => set("data_assunzione", e.target.value)} data-testid="cop-assunzione" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Inizio contratto</Label><Input type="date" value={f.contratto_inizio} onChange={(e) => set("contratto_inizio", e.target.value)} data-testid="cop-inizio" /></div>
            {!senzaScad && <div><Label className="text-xs font-semibold mb-1 block">Scadenza contratto</Label><Input type="date" value={f.scadenza_contratto} onChange={(e) => set("scadenza_contratto", e.target.value)} data-testid="cop-scadenza" /></div>}
          </div>
          <div className="mt-3"><Label className="text-xs font-semibold mb-1 block">Note</Label><Input value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="cop-note" /></div>
        </div>

        <div className="flex justify-end">
          <Button onClick={finish} disabled={saving} className="font-semibold" data-testid="cop-finish">{saving ? "Salvataggio..." : <><Check size={16} className="mr-1" /> Registra operatore</>}</Button>
        </div>
      </div>

      <Dialog open={!!newOp} onOpenChange={(o) => { if (!o) skipAccount(); }}>
        <DialogContent className="max-w-md" data-testid="cop-account-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound size={18} /> Crea l'account di accesso?</DialogTitle>
            <DialogDescription>Operatore <b>{newOp?.cognome} {newOp?.nome}</b> registrato. Vuoi creargli subito un account di login collegato? (Potrà accedere a "Il mio account" e, se gli assegni permessi, ad altre aree.)</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold mb-1 block">Email di accesso</Label><Input type="email" value={acc.email} onChange={(e) => setAcc((p) => ({ ...p, email: e.target.value }))} placeholder="nome.cognome@habitat.it" data-testid="cop-acc-email" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Password</Label><Input value={acc.password} onChange={(e) => setAcc((p) => ({ ...p, password: e.target.value }))} placeholder="lascia vuoto per una password provvisoria" data-testid="cop-acc-password" /></div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Ruolo</Label>
              <select value={acc.role} onChange={(e) => setAcc((p) => ({ ...p, role: e.target.value }))} className="h-10 w-full rounded-md border px-3 text-sm" data-testid="cop-acc-role">
                {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={skipAccount} data-testid="cop-acc-skip">Salta per ora</Button>
            <Button onClick={createAccount} disabled={creating} className="font-semibold" data-testid="cop-acc-create">{creating ? "Creazione..." : "Crea account"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
